import { useContext, useCallback } from 'react';
import { BeerContext } from '../BeerContext';
import { 
  ToggleFavoriteAction,
  UpdateRatingAction,
  UpdateNotesAction,
  ToggleTastingListAction,
  SetUserInteractionsAction
} from '../actions/userInteractionActions';
import { UserInteractionsState } from '../reducers/userInteractionsReducer';
import { useStorageService } from '../../services/storage/StorageServiceProvider';

/**
 * Hook for accessing and manipulating user interactions state
 * Following Interface Segregation Principle by providing a focused API
 */
export function useUserInteractions() {
  const context = useContext(BeerContext);
  const storageService = useStorageService();
  
  if (context === null) {
    throw new Error('useUserInteractions must be used within a BeerProvider');
  }
  
  const { state, dispatch } = context;
  
  // User interactions state selectors
  const userInteractions: UserInteractionsState = state.userInteractions;
  
  // User interactions action creators
  const toggleFavorite = useCallback((beerId: string) => {
    dispatch(new ToggleFavoriteAction(beerId));
    
    // Schedule storage update - action handles tasting list removal
    const updatedFavorites = [...state.userInteractions.favorites];
    const index = updatedFavorites.indexOf(beerId);
    
    let updatedTastingList = [...state.userInteractions.tastingList];
    
    if (index === -1) {
      updatedFavorites.push(beerId);
      
      // Remove from tasting list if present (matches action logic)
      const tastingIndex = updatedTastingList.indexOf(beerId);
      if (tastingIndex !== -1) {
        updatedTastingList.splice(tastingIndex, 1);
      }
    } else {
      updatedFavorites.splice(index, 1);
    }
    
    storageService.setItem('favorites', JSON.stringify(updatedFavorites));
    storageService.setItem('tastingList', JSON.stringify(updatedTastingList));
  }, [dispatch, state.userInteractions.favorites, state.userInteractions.tastingList, storageService]);
  
  const updateRating = useCallback((beerId: string, rating: number) => {
    dispatch(new UpdateRatingAction({ beerId, rating }));
    
    // Schedule storage update - action handles tasting list removal
    const updatedRatings = {
      ...state.userInteractions.ratings,
      [beerId]: rating,
    };
    
    let updatedTastingList = [...state.userInteractions.tastingList];
    
    // If rating is greater than 0, remove from tasting list (matches action logic)
    if (rating > 0) {
      const tastingIndex = updatedTastingList.indexOf(beerId);
      if (tastingIndex !== -1) {
        updatedTastingList.splice(tastingIndex, 1);
      }
    }
    
    storageService.setItem('ratings', JSON.stringify(updatedRatings));
    storageService.setItem('tastingList', JSON.stringify(updatedTastingList));
  }, [dispatch, state.userInteractions.ratings, state.userInteractions.tastingList, storageService]);
  
  const updateNotes = useCallback((beerId: string, notes: string) => {
    dispatch(new UpdateNotesAction({ beerId, notes }));
    
    // Schedule storage update - action handles tasting list removal
    const updatedNotes = {
      ...state.userInteractions.notes,
      [beerId]: notes,
    };
    
    let updatedTastingList = [...state.userInteractions.tastingList];
    
    // If notes are not empty, remove from tasting list (matches action logic)
    if (notes && notes.trim() !== '') {
      const tastingIndex = updatedTastingList.indexOf(beerId);
      if (tastingIndex !== -1) {
        updatedTastingList.splice(tastingIndex, 1);
      }
    }
    
    storageService.setItem('notes', JSON.stringify(updatedNotes));
    storageService.setItem('tastingList', JSON.stringify(updatedTastingList));
  }, [dispatch, state.userInteractions.notes, state.userInteractions.tastingList, storageService]);
  
  const toggleTastingList = useCallback((beerId: string) => {
    dispatch(new ToggleTastingListAction(beerId));
    
    // Schedule storage update
    const updatedTastingList = [...state.userInteractions.tastingList];
    const index = updatedTastingList.indexOf(beerId);
    
    if (index === -1) {
      updatedTastingList.push(beerId);
    } else {
      updatedTastingList.splice(index, 1);
    }
    
    storageService.setItem('tastingList', JSON.stringify(updatedTastingList));
  }, [dispatch, state.userInteractions.tastingList, storageService]);
  
  const setUserInteractions = useCallback((userInteractions: UserInteractionsState) => {
    dispatch(new SetUserInteractionsAction(userInteractions));
  }, [dispatch]);
  
  // Helper methods for checking interaction status
  const isFavorite = useCallback((beerId: string) => {
    return state.userInteractions.favorites.includes(beerId);
  }, [state.userInteractions.favorites]);
  
  const getRating = useCallback((beerId: string) => {
    return state.userInteractions.ratings[beerId] || 0;
  }, [state.userInteractions.ratings]);
  
  const getNotes = useCallback((beerId: string) => {
    return state.userInteractions.notes[beerId] || '';
  }, [state.userInteractions.notes]);
  
  const isInTastingList = useCallback((beerId: string) => {
    return state.userInteractions.tastingList.includes(beerId);
  }, [state.userInteractions.tastingList]);
  
  return {
    // State
    userInteractions,
    
    // Actions
    toggleFavorite,
    updateRating,
    updateNotes,
    toggleTastingList,
    setUserInteractions,
    
    // Selectors
    isFavorite,
    getRating,
    getNotes,
    isInTastingList,
  };
}
